// TODO: unspaghettify this

import {
  jsonCopy,
  LucideIconName,
  WebsiteConfigPage,
} from '@bm-js/atlascloud-shared'
import { useMemo, useState } from 'react'
import SitemapSortable from './Sitemap/SitemapSortable'
import InfoBox from './InfoBox'
import { Button, CText, Icon } from '@bm-js/atlascloud-ui'
import LinkButton from './LinkButton'
import { useEditorActions, useEditorData } from '../utils/hooks'
import PageForm from './PageForm'
import { addPageToPages } from '../utils/editor.utils'
import { useTr } from '../utils/useTr'
import { TrKey } from '../cnst/translations/trKey.cnst'
import { useForm } from '../hooks/useForm'

const Sitemap = () => {
  const { tr } = useTr()
  const { patchConfig, setActivePage } = useEditorActions()
  const { workingConfig } = useEditorData()
  const { features } = useEditorData().backendResponse.website

  const addPageForm = useForm({
    formComponent: PageForm,
    extras: { features },
  })

  const [rootPage, setRootPage] = useState<WebsiteConfigPage | null>(null)
  const [parentPage, setParentPage] = useState<WebsiteConfigPage | null>(null)
  const [childPage, setChildPage] = useState<WebsiteConfigPage | null>(null)

  const addPage = async () => {
    const result = await addPageForm({})
    if (result.status !== 'success') return
    const asChildOfUuid = childPage?.uuid || parentPage?.uuid || rootPage?.uuid
    const updatedPages = addPageToPages(
      workingConfig.pages,
      result.data,
      asChildOfUuid
    )
    setPages(updatedPages)
    setActivePage(result.data.uuid)
  }

  const setPages = (pages: WebsiteConfigPage[]) => {
    // For some reason, id is added to the page object by the dnd lib
    // This breaks the backend save because it thinks this is the object id
    const deletePageIdRecursive = (
      pages: WebsiteConfigPage[]
    ): WebsiteConfigPage[] => {
      return pages.map((page) => ({
        ...page,
        id: undefined,
        children: page.children
          ? deletePageIdRecursive(page.children)
          : undefined,
      }))
    }

    patchConfig({ pages: deletePageIdRecursive(pages) })
  }

  const _pages = useMemo(() => {
    if (rootPage) {
      const rootPageIndex = workingConfig.pages.findIndex(
        (page) => page.uuid === rootPage.uuid
      )

      if (parentPage) {
        const parentPageIndex = workingConfig.pages[
          rootPageIndex
        ].children!.findIndex((page) => page.uuid === parentPage.uuid)

        if (childPage) {
          const childPageIndex = workingConfig.pages[rootPageIndex].children![
            parentPageIndex
          ].children!.findIndex((page) => page.uuid === childPage.uuid)
          return workingConfig.pages[rootPageIndex].children![parentPageIndex]
            .children![childPageIndex].children!
        }

        return workingConfig.pages[rootPageIndex].children![parentPageIndex]
          .children!
      }

      if (!workingConfig.pages[rootPageIndex]?.children)
        return workingConfig.pages
      return workingConfig.pages[rootPageIndex].children!
    }
    return workingConfig.pages
  }, [rootPage, parentPage, childPage, workingConfig.pages])

  const parseSetPages = (
    updatedPages: WebsiteConfigPage[],
    returnInsteadOfSet?: boolean
  ) => {
    let updated: WebsiteConfigPage[] = []
    if (!rootPage) {
      updated = updatedPages
    }

    if (rootPage && !parentPage) {
      updated = workingConfig.pages.map((page) => ({
        ...page,
        children: page.uuid !== rootPage.uuid ? page.children : updatedPages,
      }))
    }

    if (rootPage && parentPage && !childPage) {
      updated = workingConfig.pages.map((page) => ({
        ...page,
        children:
          page.uuid !== rootPage.uuid
            ? page.children
            : page.children?.map((child) => ({
                ...child,
                children:
                  child.uuid !== parentPage.uuid
                    ? child.children
                    : updatedPages,
              })),
      }))
    }

    if (returnInsteadOfSet) {
      return updated
    } else setPages(updated)
  }

  const selectPage = (page: WebsiteConfigPage | null) => {
    if (!page) return
    setActivePage(page.uuid)

    if (!rootPage) {
      setRootPage(page)
      return
    }

    if (!parentPage) {
      setParentPage(page)
      return
    }

    if (!childPage) {
      setChildPage(page)
      return
    }
  }

  const handleBack = () => {
    if (childPage) {
      if (parentPage) setActivePage(parentPage.uuid)
      setChildPage(null)
      return
    }

    if (parentPage) {
      if (rootPage) setActivePage(rootPage.uuid)
      setParentPage(null)
      return
    }

    if (rootPage) {
      setRootPage(null)
      return
    }
  }

  const pageAbove = useMemo(() => {
    if (childPage) {
      return `'${parentPage?.viewName}'`
    }

    if (parentPage) {
      return `'${rootPage?.viewName}'`
    }

    if (rootPage) {
      return tr(TrKey.editorSitemapTopLevel)
    }

    return null
  }, [rootPage, parentPage, childPage, tr])

  const currentLevel = useMemo(() => {
    if (childPage) {
      return `'${childPage.viewName}'`
    }
    if (parentPage) {
      return `'${parentPage.viewName}'`
    }
    if (rootPage) {
      return `'${rootPage.viewName}'`
    }
    return tr(TrKey.editorSitemapTopLevel)
  }, [rootPage, parentPage, childPage, tr])

  const movePageDown = (
    _updatedPages: WebsiteConfigPage[],
    pageToMove: WebsiteConfigPage
  ) => {
    const updatedPages = jsonCopy(_updatedPages)
    const pageIndex = updatedPages.findIndex(
      (page) => page.uuid === pageToMove.uuid
    )
    const [reordered] = updatedPages.splice(pageIndex, 1)
    updatedPages[pageIndex].children!.push(reordered)

    parseSetPages(updatedPages)
  }

  const movePageUp = (
    _updatedPages: WebsiteConfigPage[],
    pageToMove: WebsiteConfigPage
  ) => {
    const updatedPages = jsonCopy(_updatedPages)
    const pageIndex = updatedPages.findIndex(
      (page) => page.uuid === pageToMove.uuid
    )
    const [reordered] = updatedPages.splice(pageIndex, 1)
    const parsed = parseSetPages(updatedPages, true) as WebsiteConfigPage[]

    const updatedTopLevelPages = jsonCopy(parsed)

    if (rootPage) {
      updatedTopLevelPages.push(reordered)
    }

    if (parentPage && rootPage) {
      const rootPageIndex = updatedTopLevelPages.findIndex(
        (page) => page.uuid === rootPage.uuid
      )
      updatedTopLevelPages[rootPageIndex].children!.push(reordered)
    }

    if (childPage && parentPage && rootPage) {
      const rootPageIndex = updatedTopLevelPages.findIndex(
        (page) => page.uuid === rootPage.uuid
      )
      const parentPageIndex = updatedTopLevelPages[
        rootPageIndex
      ].children!.findIndex((page) => page.uuid === parentPage.uuid)

      updatedTopLevelPages[rootPageIndex].children![
        parentPageIndex
      ].children!.push(reordered)
    }

    setPages(updatedTopLevelPages)
  }

  return (
    <div>
      <div className='flex items-center justify-between mb-2 gap-2'>
        <CText className='text-primary-400 text-sm'>
          {tr(TrKey.editorSitemapEditing)} {currentLevel}
        </CText>
        <InfoBox w={220} text={tr(TrKey.editorSitemapEditingTooltip)} />
      </div>
      {!!pageAbove && (
        <LinkButton
          className='mb-2 flex items-center w-full'
          onClick={handleBack}
        >
          <Icon name={LucideIconName.ArrowLeft} className='size-4 shrink-0' />
          <span className='text-left truncate'>
            {tr(TrKey.editorSitemapBackTo)} {pageAbove}
          </span>
        </LinkButton>
      )}

      {_pages.length ? (
        <SitemapSortable
          canMovePageDown={!childPage}
          pageAbove={pageAbove}
          movePageDown={movePageDown}
          movePageUp={movePageUp}
          setPages={parseSetPages}
          pages={_pages}
          selectPage={selectPage}
        />
      ) : (
        <CText className='text-sm text-gray-500'>
          {tr(TrKey.editorSitemapNoPages)}
        </CText>
      )}
      <Button className='mt-2 w-full' onClick={addPage}>
        {tr(TrKey.editorSitemapAddPage)}
      </Button>
    </div>
  )
}

export default Sitemap
